// DatabaseModal.jsx

import React from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Hooks
import useDatabaseModalState from './hooks/useDatabaseModalState';

// CSS
import './DatabaseModal.css';

// Components
import DatabaseSettingsAboutTab from './about/DatabaseSettingsAboutTab';
import DatabaseSettingsAutomationsTab from './automations/DatabaseSettingsAutomationsTab';
import DatabaseModalOverlays from './DatabaseModalOverlays';
import DatabaseSettingsFieldsTab from './DatabaseSettingsFieldsTab';
import {
  SETTINGS_TAB_ABOUT,
  SETTINGS_TAB_AUTOMATIONS,
  SETTINGS_TAB_FIELDS,
  SETTINGS_TAB_FORMS,
  SETTINGS_TAB_RELATED,
  SETTINGS_TAB_ROLLUPS,
  SETTINGS_TAB_VIEW_GROUPS,
  SETTINGS_TAB_VIEWS,
  SETTINGS_TAB_WORKFLOWS,
} from './databaseSettingsTabs';
import DatabaseSettingsFormsTab from './forms/DatabaseSettingsFormsTab';
import DatabaseSettingsMissionsTab from './missions/DatabaseSettingsMissionsTab';
import DatabaseSettingsRelatedTab from './related/DatabaseSettingsRelatedTab';
import DatabaseSettingsRollupsTab from './rollups/DatabaseSettingsRollupsTab';
import DatabaseSettingsViewGroupsTab from './viewgroups/DatabaseSettingsViewGroupsTab';
import DatabaseSettingsViewsTab from './views/DatabaseSettingsViewsTab';
import Modal from 'components/modal/Modal';
import Tabs from 'components/tabs/Tabs';

/**
 * DatabaseModal
 *
 * Renders type metadata settings, field management, and destructive delete controls for a node type.
 *
 * @param {boolean} isOpen - Whether the modal is open.
 * @param {function} setOpen - Function to toggle modal visibility.
 * @param {string} typeKey - Active type key from database context.
 * @param {object|null} typeDef - Active type definition object.
 * @param {string|null} baseKey - Active base key.
 * @param {object|null} [activeDatabase] - Active database document data.
 * @param {string|null} [userKey] - Current user key for automation saves.
 * @param {array} [resolvedProperties] - Resolved field definitions for automation editing.
 * @param {array} [nodeTypeOptions] - Base node type options for email actions.
 * @param {function} [onAutomationsSaved] - Updates parent database state after automation saves.
 * @param {object[]} [views] - Custom views available for the active database.
 * @param {string|null} [selectedViewId] - Currently selected view key.
 * @param {function} [onSelectView] - Selects a view from the settings modal.
 * @param {function} [onOpenImport] - Opens the CSV import modal.
 * @param {function} [onExport] - Downloads the CSV export directly.
 * @param {boolean} [canPublishSchema=false] - Whether the publish-schema button should be shown.
 * @param {function} [onPublishSchema] - Publishes the current type schema to the library.
 * @param {boolean} [publishingSchema=false] - Whether schema publishing is in progress.
 * @param {string} [initialTab='about'] - Tab to show when the modal opens.
 * @param {object|null} [viewEditorRequest] - Optional request to open the view editor on a specific mode.
 * @param {function} [onViewEditorRequestHandled] - Clears the pending view editor request after it is consumed.
 * @param {object|null} [workflowEditorRequest] - Optional request to open the workflow designer for a template key.
 * @param {function} [onWorkflowEditorRequestHandled] - Clears the pending workflow editor request after it is consumed.
 *
 * @returns {ReactNode} The rendered modal and confirmations.
 */
const DatabaseModal = React.memo(({
  isOpen,
  setOpen,
  typeKey,
  typeDef,
  baseKey,
  activeDatabase = null,
  userKey = null,
  resolvedProperties = [],
  nodeTypeOptions = [],
  onAutomationsSaved,
  views = [],
  selectedViewId = null,
  onSelectView,
  onOpenImport,
  onExport,
  canPublishSchema = false,
  onPublishSchema,
  publishingSchema = false,
  initialTab = SETTINGS_TAB_ABOUT,
  viewEditorRequest = null,
  onViewEditorRequestHandled,
  workflowEditorRequest = null,
  onWorkflowEditorRequestHandled,
  previewNodeKeyOverride = '',
  nodeModule = null,
}) => {

  // ----- DEVICE DETECTION ----- //

  const isMobile = useIsMobile();

  // ----- HOOKS ----- //

  const {
    modalUiState,
    aboutTabState,
    relatedTabState,
    viewGroupsTabState,
    viewsTabState,
    formsTabState,
    rollupsTabState,
    missionsTabState,
    automationsTabState,
    overlaysState,
  } = useDatabaseModalState({
    isOpen,
    setOpen,
    typeKey,
    typeDef,
    baseKey,
    activeDatabase,
    userKey,
    resolvedProperties,
    nodeTypeOptions,
    onAutomationsSaved,
    views,
    initialTab,
    viewEditorRequest,
    onViewEditorRequestHandled,
    workflowEditorRequest,
    onWorkflowEditorRequestHandled,
    nodeModule,
    previewNodeKeyOverride,
    onSelectView,
  });

  const schemaFields = typeDef?.schema?.fields || [];
  const aboutTabProps = {
    ...aboutTabState,
    onOpenImport: aboutTabState.allowImport === false ? undefined : onOpenImport,
    onExport,
    canPublishSchema,
    onPublishSchema,
    publishingSchema,
  };
  const viewsTabProps = {
    ...viewsTabState,
    views,
    selectedViewId,
    onSelectView,
  };
  const formsTabProps = {
    ...formsTabState,
  };
  const rollupsTabProps = {
    ...rollupsTabState,
    fields: schemaFields,
  };
  const missionsTabProps = {
    ...missionsTabState,
  };
  const overlaysProps = {
    ...overlaysState,
    typeKey,
    baseKey,
    typeLabel: String(typeDef?.label || '').trim(),
    views,
    nodeTypeOptions,
    schemaFields,
  };

  // ----- RENDER ----- //

  return (
    <>

      <Modal
        iconKey="settings"
        title={
          (typeDef?.system === true || typeDef?.isSystem === true)
            ? `${modalUiState.typeTitlePlural} / Views`
            : `${modalUiState.typeTitlePlural} / Collection Settings`
        }
        isOpen={isOpen}
        width={isMobile ? "100%" : 744}
        height={890}
        onClose={() => setOpen(false)}
      >
        <div className="database-modal-container">
          <Tabs
            tabs={modalUiState.settingsTabs}
            selectedKey={modalUiState.selectedTab}
          />

          {modalUiState.selectedTab === SETTINGS_TAB_ABOUT && (
            <DatabaseSettingsAboutTab {...aboutTabProps} />
          )}

          {modalUiState.selectedTab === SETTINGS_TAB_FIELDS && (
            <DatabaseSettingsFieldsTab
              typeKey={typeKey}
              typeDef={typeDef}
              baseKey={baseKey}
            />
          )}

          {modalUiState.selectedTab === SETTINGS_TAB_RELATED && (
            <DatabaseSettingsRelatedTab {...relatedTabState} />
          )}

          {modalUiState.selectedTab === SETTINGS_TAB_FORMS && (
            <DatabaseSettingsFormsTab {...formsTabProps} />
          )}

          {modalUiState.selectedTab === SETTINGS_TAB_ROLLUPS && (
            <DatabaseSettingsRollupsTab {...rollupsTabProps} />
          )}

          {modalUiState.selectedTab === SETTINGS_TAB_WORKFLOWS && (
            <DatabaseSettingsMissionsTab {...missionsTabProps} />
          )}

          {modalUiState.selectedTab === SETTINGS_TAB_VIEW_GROUPS && (
            <DatabaseSettingsViewGroupsTab {...viewGroupsTabState} />
          )}

          {modalUiState.selectedTab === SETTINGS_TAB_VIEWS && (
            <DatabaseSettingsViewsTab {...viewsTabProps} />
          )}

          {modalUiState.selectedTab === SETTINGS_TAB_AUTOMATIONS && (
            <DatabaseSettingsAutomationsTab {...automationsTabState} />
          )}

        </div>

      </Modal>

      <DatabaseModalOverlays {...overlaysProps} />
    </>
  );
});

// Dev Tools
DatabaseModal.displayName = 'DatabaseModal';

export default DatabaseModal;
